import { addSeconds, parseISO } from 'date-fns'

import { env } from '@/config/env'
import type { Event, EventKind } from '@/services/api/schemas/events'

import { formatTime, plural } from './format'

export const EVENT_TYPE_LABELS: Record<EventKind, string> = {
  conflict: 'Конфликт',
  smoke_fire: 'Дым / возгорание',
  cashier_missing: 'Отсутствие кассира',
  security_overstay: 'Охрана',
  unknown: 'Событие',
}

/** CSS-переменная цвета типа события. */
export const EVENT_TYPE_COLOR_VAR: Record<EventKind, string> = {
  conflict: 'var(--ev-conflict)',
  smoke_fire: 'var(--ev-fire)',
  cashier_missing: 'var(--ev-cashier)',
  security_overstay: 'var(--ev-guard)',
  unknown: 'var(--ink-3)',
}

/**
 * Заголовок события в списках: для кассира добавляем номер кассы, у охраны берём подпись
 * бэкенда («Охрана: длительное сидение») — одно «Охрана» не говорит, что случилось.
 */
export function getEventTitle(event: Event): string {
  if (event.type === 'cashier_missing' && event.cash_desk_number !== undefined) {
    return `${EVENT_TYPE_LABELS.cashier_missing} — касса №${event.cash_desk_number}`
  }
  if (event.type === 'security_overstay' || event.type === 'unknown') {
    return event.label ?? EVENT_TYPE_LABELS[event.type]
  }
  return EVENT_TYPE_LABELS[event.type]
}

const ALERT_CLASS_LABELS: Record<string, string> = { smoke: 'Дым', fire: 'Возгорание' }

/** Уточнение для дыма/возгорания: подпись бэкенда или расшифровка alert_class. */
export function getEventSubtype(event: Event): string | undefined {
  if (event.type !== 'smoke_fire') return undefined
  return (
    event.label ??
    (event.alert_class ? ALERT_CLASS_LABELS[event.alert_class] : undefined) ??
    event.alert_class
  )
}

const CAMERA_ZONES: Record<string, string> = {
  pump: 'ТРК',
  cashdesk: 'касса',
  hall: 'зал',
  store: 'магазин',
  entrance: 'въезд',
  yard: 'территория',
}

/** cam-pump-3 → «Камера · ТРК 3», «15» → «Камера 15». Незнакомый формат показываем как есть. */
export function formatCameraName(cameraId: string): string {
  if (/^\d+$/.test(cameraId)) return `Камера ${cameraId}`
  const match = /^cam-([a-z]+)-?(\d*)$/i.exec(cameraId)
  if (!match) return cameraId
  const zone = CAMERA_ZONES[match[1].toLowerCase()]
  if (!zone) return cameraId
  return `Камера · ${zone}${match[2] ? ` ${match[2]}` : ''}`
}

/**
 * Типы, у которых бэкенд хранит только снимок (коллаж кадров), без клипа.
 * Используется как запасное правило для старых ответов без has_photo/has_video.
 */
const TYPES_WITHOUT_VIDEO: ReadonlySet<EventKind> = new Set<EventKind>(['cashier_missing'])

/** Есть ли у события снимок (?kind=image). */
export function hasEventPhoto(event: Event): boolean {
  return event.has_photo ?? Boolean(event.has_media)
}

/** Есть ли у события видеоклип (?kind=video). */
export function hasEventVideo(event: Event): boolean {
  return event.has_video ?? (Boolean(event.has_media) && !TYPES_WITHOUT_VIDEO.has(event.type))
}

/** Абсолютный адрес снимка или видео события; undefined, если такого медиа нет. */
export function getEventMediaUrl(event: Event, kind: 'image' | 'video'): string | undefined {
  if (kind === 'image' ? !hasEventPhoto(event) : !hasEventVideo(event)) return undefined
  const path = event.media_url ?? `/api/v1/events/${encodeURIComponent(event.id)}/media`
  const separator = path.includes('?') ? '&' : '?'
  return `${env.apiUrl}${path}${separator}kind=${kind}`
}

interface EventInterval {
  start?: string
  end?: string
}

function getInterval(startIso: string, seconds: number | undefined): EventInterval {
  const start = formatTime(startIso)
  if (seconds === undefined) return { start }
  return { start, end: formatTime(addSeconds(parseISO(startIso), seconds).toISOString()) }
}

/** Начало и окончание отсутствия кассира по данным события. */
export const getAbsenceInterval = (event: Event): EventInterval =>
  getInterval(event.absence_started_at ?? event.detected_at, event.absence_seconds)

/** Начало и окончание инцидента охраны (охранник дольше порога на посту). */
export const getGuardIncidentInterval = (event: Event): EventInterval =>
  getInterval(event.guard_incident_started_at ?? event.detected_at, event.guard_incident_seconds)

export function formatDurationSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} ${plural(seconds, 'секунда', 'секунды', 'секунд')}`
  const minutes = Math.round(seconds / 60)
  return `${minutes} ${plural(minutes, 'минута', 'минуты', 'минут')}`
}

export const isCriticalEvent = (event: Event): boolean =>
  event.type === 'conflict' || event.type === 'smoke_fire'
